import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGames } from '../../store/slices/gameSlice';
import type { GameStatus } from '@sulis-liiga/common';
import GameCard from '../../components/game/GameCard';

const GameListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { games, loading, error } = useSelector((state: RootState) => state.game);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [divisionFilter] = useState('');

  useEffect(() => {
    const filters: { status?: GameStatus; divisionId?: string } = {};
    if (statusFilter) filters.status = statusFilter as GameStatus;
    if (divisionFilter) filters.divisionId = divisionFilter;
    dispatch(fetchGames(filters));
  }, [dispatch, statusFilter, divisionFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6">
          <div className="rounded-md bg-danger-50 p-4">
            <p className="text-sm font-medium text-danger-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Games</h2>
          <div className="mt-4 flex space-x-3 md:mt-0">
            <Link
              to="/games/proposals"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Proposals
            </Link>
            <Link
              to="/games/proposals/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              Propose Game
            </Link>
          </div>
        </div>

        <div className="mb-6 flex space-x-4">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="disputed">Disputed</option>
          </select>
        </div>

        {games.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">No games found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters or propose a new game.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {games.map(game => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameListPage;
